/**
 * The α of the `candleα` wordmark, as geometry rather than a text glyph.
 *
 * One source of truth for three renderers that cannot share a DOM: the header
 * and static-page marks (`AlphaMark`, an inline SVG), the Daily challenge
 * Result card (a 2D canvas), and the Open Graph image (`scripts/renderOgImage.ts`,
 * a canvas on Node). Drawing it as a path rather than typing "α" is what keeps
 * the mark identical whatever font the surface resolves, and what lets it be
 * split down the middle into the candle-up / candle-down pair every other
 * direction in the UI is drawn in.
 *
 * The path is the same outline `public/favicon.svg` draws.
 */

/** The outline, in the path's own coordinates (y up, as in a font). */
export const ALPHA_PATH =
  "M144 -14Q83 -14 56.0 35.5Q29 85 29 171Q29 263 59.0 332.5Q89 402 142.5 440.5Q196 479 265 479Q301 479 324.0 464.0Q347 449 360.5 426.5Q374 404 380.5 378.5Q387 353 390 332H412L404 244Q404 217 404.5 185.5Q405 154 409.5 126.5Q414 99 425.0 81.5Q436 64 457 64Q480 64 495.5 75.5Q511 87 525 103L541 87Q535 78 525.0 61.5Q515 45 499.0 28.5Q483 12 460.0 0.5Q437 -11 407 -11Q372 -11 357.5 13.0Q343 37 343 74Q343 92 346.0 113.0Q349 134 351 159H343Q314 112 281.5 72.5Q249 33 215.0 9.5Q181 -14 144 -14ZM166 74Q202 74 236.0 94.5Q270 115 297.5 149.0Q325 183 341.0 224.0Q357 265 357 306Q357 353 332.5 374.5Q308 396 260 396Q179 396 127.0 345.5Q75 295 75 194Q75 129 103.5 101.5Q132 74 166 74ZM393 228 389 315Q407 361 432.5 404.5Q458 448 474 470L557 472L558 457Q550 447 532.0 423.5Q514 400 492.0 370.5Q470 341 449.0 311.0Q428 281 413.0 258.5Q398 236 393 228Z";

/** The mark is drawn in a square box this many units on a side. */
export const ALPHA_VIEW_BOX = 24;

/**
 * Path units to box units. The negative y flips the path's font-style axis
 * (y up) into the box's screen axis (y down), so any renderer applying this
 * has to expect a mirrored coordinate system inside it.
 */
export const ALPHA_TRANSLATE = { x: -0.206, y: 21.669 } as const;
export const ALPHA_SCALE = 0.04159;

/** The same pair as an SVG `transform` attribute. */
export const ALPHA_TRANSFORM = `translate(${ALPHA_TRANSLATE.x},${ALPHA_TRANSLATE.y}) scale(${ALPHA_SCALE},-${ALPHA_SCALE})`;

/** Where the up half ends and the down half begins: the box's vertical centre. */
export const ALPHA_SPLIT_X = ALPHA_VIEW_BOX / 2;

/** The outline's extents in path units, measured off the path data once. */
const PATH_BOUNDS = { minX: 29, maxX: 558, minY: -14, maxY: 479 } as const;

const toBoxX = (x: number) => x * ALPHA_SCALE + ALPHA_TRANSLATE.x;
const toBoxY = (y: number) => ALPHA_TRANSLATE.y - y * ALPHA_SCALE;

/**
 * The inked part of the box, in box units. The outline does not fill its box
 * (it sits inset by about one unit on each side), so a caller laying the mark
 * out beside text has to advance by the ink, not by the box, or the wordmark
 * gets a hole in it. `AlphaMark` never needs this (CSS sizes the box), but the
 * two canvas renderers position the mark arithmetically and do.
 */
export const ALPHA_INK = {
  x: toBoxX(PATH_BOUNDS.minX),
  y: toBoxY(PATH_BOUNDS.maxY),
  width: toBoxX(PATH_BOUNDS.maxX) - toBoxX(PATH_BOUNDS.minX),
  height: toBoxY(PATH_BOUNDS.minY) - toBoxY(PATH_BOUNDS.maxY),
} as const;

/** The ink's proportions: how wide it is for a given height. */
export const ALPHA_INK_ASPECT = ALPHA_INK.width / ALPHA_INK.height;

/**
 * The outline's own stroke thickness, as a fraction of its ink height.
 * Measured off a pixel scan of the filled path across the bowl's near-vertical
 * left side, which is the one place a horizontal run is the true thickness.
 * The outline is a hairline by design (it was drawn to sit beside 16px UI
 * text), so any larger setting has to put weight back on with a stroke.
 */
export const ALPHA_NATURAL_STEM_RATIO = 0.092;

/**
 * JetBrains Mono's own metrics, as fractions of the font size. Measured off
 * the registered face rather than assumed, and stable across sizes:
 *
 * - `ASCENT`: the height of a `d`/`l` stem above the baseline. The mark is set
 *   to exactly this, so the wordmark has one flat ascender line across it.
 * - `MIDDLE_TO_BASELINE`: how far the alphabetic baseline sits below canvas's
 *   `middle` baseline, which is the one the Result card lays text out on.
 * - `STEM`: the width of an `l` stem, per weight. The mark is stroked up to
 *   the matching one so it carries the same colour as the letters beside it.
 */
export const MONO = {
  ASCENT: 0.73,
  MIDDLE_TO_BASELINE: 0.36,
  STEM: { regular: 0.096, bold: 0.126 },
} as const;

/** The space between the word and the mark, as a fraction of the font size. */
export const ALPHA_GAP_EM = 0.12;

/**
 * How to set the mark beside `candle` at `fontSize`: the ink box it fills, the
 * stroke that brings it up to the text's weight, and the width it advances the
 * line by (the ink plus the stroke that overhangs it).
 *
 * `stemRatio` is the weight of the text it sits next to, from `MONO.STEM`.
 */
export function alphaMetrics(
  fontSize: number,
  stemRatio: number = MONO.STEM.regular,
): { inkHeight: number; inkWidth: number; lineWidth: number; advance: number } {
  const inkHeight = fontSize * MONO.ASCENT;
  const inkWidth = inkHeight * ALPHA_INK_ASPECT;
  const lineWidth = Math.max(
    0,
    fontSize * stemRatio - inkHeight * ALPHA_NATURAL_STEM_RATIO,
  );
  return { inkHeight, inkWidth, lineWidth, advance: inkWidth + lineWidth };
}

/** Where and how big to draw the mark. `inkLeft`/`baseline` are its ink, not its box. */
export interface AlphaMarkBox {
  /** Left edge of the ink. The stroke overhangs it by half `lineWidth`. */
  inkLeft: number;
  /** The text baseline the ink's bottom sits on. */
  baseline: number;
  /** Ascender height: the ink's top is this far above `baseline`. */
  inkHeight: number;
  /** Extra weight, applied as a stroke around the fill. */
  lineWidth?: number;
  up: string;
  down: string;
}

/**
 * Draw the mark onto a 2D context as two clipped halves, up on the left and
 * down on the right, its ink sitting on `baseline` and rising exactly
 * `inkHeight` above it.
 *
 * Each half is filled *and* stroked inside its own clip, so the added weight
 * takes the half's colour rather than smearing one colour across the seam.
 * The clip is bled out by a full `lineWidth` so a stroke that overhangs the
 * box is not shaved off at the card's edge of it.
 *
 * Shared by the Result card and the Open Graph render. `Path2D` is a
 * parameter because the two run in different worlds: in a browser it is a
 * global, on Node it is an export of whatever canvas implementation the caller
 * chose. Where there is neither (jsdom) the mark is skipped rather than
 * thrown over: the card's layout is what the unit tests assert, and the PNG is
 * already a best-effort result to its caller.
 */
export function fillAlpha(
  ctx: CanvasRenderingContext2D,
  mark: AlphaMarkBox,
  PathCtor: typeof Path2D | undefined = (globalThis as { Path2D?: typeof Path2D }).Path2D,
): void {
  if (!PathCtor) return;

  const path = new PathCtor(ALPHA_PATH);
  const unit = mark.inkHeight / ALPHA_INK.height;
  const boxX = mark.inkLeft - ALPHA_INK.x * unit;
  const boxY = mark.baseline - (ALPHA_INK.y + ALPHA_INK.height) * unit;
  const boxSize = ALPHA_VIEW_BOX * unit;
  const splitX = boxX + ALPHA_SPLIT_X * unit;
  const lineWidth = mark.lineWidth ?? 0;
  const bleed = lineWidth + 1;

  const halves = [
    { left: boxX - bleed, right: splitX, color: mark.up },
    { left: splitX, right: boxX + boxSize + bleed, color: mark.down },
  ];

  for (const half of halves) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(half.left, boxY - bleed, half.right - half.left, boxSize + 2 * bleed);
    ctx.clip();
    ctx.translate(boxX, boxY);
    ctx.scale(unit, unit);
    ctx.translate(ALPHA_TRANSLATE.x, ALPHA_TRANSLATE.y);
    ctx.scale(ALPHA_SCALE, -ALPHA_SCALE);
    ctx.fillStyle = half.color;
    ctx.fill(path);
    if (lineWidth > 0) {
      ctx.strokeStyle = half.color;
      // The context is scaled into path units by now, so the stroke has to be
      // expressed in them too, or it would come out `unit * ALPHA_SCALE` thin.
      ctx.lineWidth = lineWidth / (unit * ALPHA_SCALE);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.stroke(path);
    }
    ctx.restore();
  }
}
